import { ChevronLeft, ChevronRight, Expand, Pencil, Shrink, Sparkles, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";

import {
  API_PREFIX,
  ApiError,
  getUnitReference,
  listUnitInstances,
  MAX_LIMIT,
  mergeUnit,
  patchUnit,
  reviewInstance,
} from "@/api/client";
import type {
  CodeParts,
  Instance,
  ReviewCreate,
  Shape,
  TypeDetails,
  UnitReference,
} from "@/api/client";
import { useCan } from "@/auth/AuthProvider";
import { feetInches } from "@/flow/takeoff";
import { RenameDialog } from "@/flow/RenameDialog";
import { useTakeoff } from "@/hooks/useTakeoff";
import { useUnitAnnotation } from "@/hooks/useUnitAnnotation";
import { Badge } from "@/ui/Badge";
import { Button } from "@/ui/Button";
import { cn } from "@/ui/cn";
import { Notice } from "@/ui/Notice";
import { Kbd, Spinner } from "@/ui/primitives";
import { AnnotationLayer, AnnotationToolbar, MARK_COLOURS } from "@/viewer/AnnotationLayer";
import type { Mode } from "@/viewer/AnnotationLayer";
import { onlyPage, replacePage } from "@/viewer/annotationGeom";
import { TypeViewer, windowAround } from "@/viewer/TypeViewer";

/** How many panels of a type the strip shows; enough to pick a clear one. */
const STRIP = 60;

const DETAILS: readonly { key: Exclude<keyof TypeDetails, "extra">; label: string }[] = [
  { key: "system", label: "System" },
  { key: "glazing", label: "Glazing" },
  { key: "frame_finish", label: "Frame finish" },
  { key: "hardware", label: "Hardware" },
  { key: "operation", label: "Operation" },
];

const FIELD =
  "w-full rounded-md border border-line-strong bg-surface px-2.5 text-sm text-ink placeholder:text-muted focus:border-accent focus:outline-none";

const message = (err: unknown, fallback: string) =>
  err instanceof ApiError ? err.detail : fallback;

const typing = (target: EventTarget | null) =>
  target instanceof HTMLElement &&
  (target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName));

/**
 * A learned code part by part (Phase 7 K3): each part's value and how many past panels
 * agree. A part the past take-offs split on is the estimator's call.
 */
function CodePartsRow({ parts }: { parts: CodeParts }) {
  const open = parts.segments.filter((p) => p.open);
  return (
    <div className="flex flex-col gap-1.5">
      <ul aria-label="Code parts" className="flex flex-wrap gap-1.5">
        {parts.segments.map((p) => (
          <li
            key={p.key}
            title={`${p.label}: ${Math.round(p.share * 100)}% of past panels`}
            className={cn(
              "flex flex-col rounded-md border px-2 py-1 text-center",
              p.open ? "border-warn bg-warn-soft" : "border-line bg-surface",
            )}
          >
            <span className="font-mono text-sm font-semibold text-ink">{p.value}</span>
            <span className="text-[10px] text-muted">
              {p.label} · {Math.round(p.share * 100)}%
            </span>
          </li>
        ))}
        {parts.flags
          .filter((f) => f.on || f.open)
          .map((f) => (
            <li
              key={f.flag}
              title={f.meaning || f.flag}
              className={cn(
                "flex flex-col rounded-md border px-2 py-1 text-center",
                f.open ? "border-warn bg-warn-soft" : "border-line bg-surface",
              )}
            >
              <span className="font-mono text-sm font-semibold text-ink">
                {f.on ? f.flag : `${f.flag}?`}
              </span>
              <span className="text-[10px] text-muted">{Math.round(f.share * 100)}%</span>
            </li>
          ))}
      </ul>
      {open.length > 0 ? (
        <p className="text-xs text-warn">
          Past take-offs split on the {open.map((p) => p.label.toLowerCase()).join(" and ")}: your
          call.
        </p>
      ) : null}
    </div>
  );
}

/**
 * One configuration at a time, as big as the screen allows: the panel that stands for
 * it on its own sheet, the other panels of it, and what the estimator knows about it.
 * The arrow keys move through every type in the take-off's order.
 */
export function TypeReviewScreen() {
  const { id = "", runId = "", unitId } = useParams();
  const navigate = useNavigate();
  const { takeoff, loaded, error, reload } = useTakeoff(runId);
  const canReview = useCan("review:write");
  const configurations = useMemo(() => takeoff?.configurations ?? [], [takeoff]);
  const found = unitId ? configurations.findIndex((c) => c.unit_id === unitId) : 0;
  const index = Math.max(0, found);
  const current = found === -1 ? null : (configurations[index] ?? null);
  const currentId = current?.unit_id ?? null;

  const [reference, setReference] = useState<UnitReference | null>(null);
  const [panels, setPanels] = useState<Instance[]>([]);
  const [shownId, setShownId] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [renaming, setRenaming] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [full, setFull] = useState(false);
  const { annotation, error: noteError, saveState, save } = useUnitAnnotation(currentId);
  const [mode, setMode] = useState<Mode>("pan");
  const [colour, setColour] = useState<string>(MARK_COLOURS[0]);
  const [label, setLabel] = useState("");
  const [selected, setSelected] = useState<number | null>(null);
  /** Earlier states of every mark on the type, newest last, for undo. This visit only. */
  const [history, setHistory] = useState<Shape[][]>([]);

  useEffect(() => {
    setSelected(null);
    setHistory([]);
  }, [currentId]);

  const takeoffUrl = `/documents/${id}/runs/${runId}/takeoff`;
  const typeUrl = useCallback(
    (unit: string) => `/documents/${id}/runs/${runId}/types/${unit}`,
    [id, runId],
  );

  const loadType = useCallback(async (unit: string) => {
    const [ref, page] = await Promise.all([
      getUnitReference(unit),
      listUnitInstances(unit, { limit: STRIP }),
    ]);
    return { ref, page };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setReference(null);
    setPanels([]);
    setShownId(null);
    setLoadError(null);
    if (!currentId) {
      return;
    }
    loadType(currentId)
      .then(({ ref, page }) => {
        if (!cancelled) {
          setReference(ref);
          setPanels(page.instances);
          setShownId(ref.instance?.id ?? page.instances[0]?.id ?? null);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setLoadError(message(err, "could not load this type's panels"));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [currentId, loadType]);

  const shown =
    panels.find((p) => p.id === shownId) ??
    (reference?.instance?.id === shownId ? reference.instance : null);
  const referenceId = annotation?.reference_instance_id ?? reference?.instance?.id ?? null;
  const shownWindow = useMemo(() => (shown ? windowAround(shown.bbox_norm) : null), [shown]);
  const [onSheet, setOnSheet] = useState<Instance[]>([]);
  const sheetIndex = shown?.page_index ?? null;
  useEffect(() => {
    let cancelled = false;
    setOnSheet([]);
    if (!currentId || sheetIndex === null) {
      return;
    }
    listUnitInstances(currentId, { pageIndex: sheetIndex, limit: MAX_LIMIT })
      .then((page) => {
        if (!cancelled) {
          setOnSheet(page.instances);
        }
      })
      .catch(() => {
        // The outlines are context; the panel itself is already on screen.
      });
    return () => {
      cancelled = true;
    };
  }, [currentId, sheetIndex]);
  const others = useMemo(
    () => (shown ? onSheet.filter((p) => p.id !== shown.id).map((p) => p.bbox_norm) : []),
    [onSheet, shown],
  );

  const go = useCallback(
    (to: number) => {
      const next = configurations[to];
      if (next) {
        void navigate(typeUrl(next.unit_id), { replace: true });
      }
    },
    [configurations, navigate, typeUrl],
  );

  const step = useCallback(
    (by: number) => {
      if (!panels.length) {
        return;
      }
      const at = panels.findIndex((p) => p.id === shownId);
      const next = panels[Math.min(panels.length - 1, Math.max(0, at + by))];
      if (next) {
        setShownId(next.id);
      }
    },
    [panels, shownId],
  );

  const allShapes = useMemo(() => annotation?.shapes ?? [], [annotation]);
  const pageShapes = shown ? onlyPage(allShapes, shown.page_id) : [];
  const elsewhere = shown ? allShapes.filter((s) => s.page_id !== shown.page_id) : [];

  const saveShapes = useCallback(
    (next: Shape[]) => {
      setHistory((h) => [...h, allShapes]);
      setSelected(null);
      void save({ shapes: next });
    },
    [allShapes, save],
  );

  const draw = (shape: Shape) => {
    saveShapes([...allShapes, shape]);
    if (shape.kind === "text") {
      // One label per placement; the next click on the sheet should not drop another.
      setMode("pan");
    }
  };

  const removeSelected = useCallback(() => {
    if (!shown || selected === null) {
      return;
    }
    const kept = onlyPage(allShapes, shown.page_id).filter((_, i) => i !== selected);
    saveShapes(replacePage(allShapes, shown.page_id, kept));
  }, [allShapes, saveShapes, selected, shown]);

  const undo = useCallback(() => {
    const previous = history[history.length - 1];
    if (!previous) {
      return;
    }
    setHistory((h) => h.slice(0, -1));
    setSelected(null);
    void save({ shapes: previous });
  }, [history, save]);

  useEffect(() => {
    const onFull = () => setFull(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", onFull);
    return () => document.removeEventListener("fullscreenchange", onFull);
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (renaming || typing(event.target)) {
        return;
      }
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "z") {
        event.preventDefault();
        if (canReview) undo();
        return;
      }
      if (event.metaKey || event.ctrlKey || event.altKey) {
        return;
      }
      if ((event.key === "Delete" || event.key === "Backspace") && selected !== null) {
        if (!canReview) return;
        event.preventDefault();
        removeSelected();
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        go(index + 1);
      } else if (event.key === "ArrowLeft") {
        event.preventDefault();
        go(index - 1);
      } else if (event.key === "ArrowDown") {
        event.preventDefault();
        step(1);
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        step(-1);
      } else if (event.key === "Escape" && !document.fullscreenElement) {
        void navigate(takeoffUrl);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go, index, step, navigate, takeoffUrl, renaming, undo, removeSelected, selected, canReview]);

  const toggleFull = () => {
    if (document.fullscreenElement) {
      void document.exitFullscreen?.();
    } else {
      void document.documentElement.requestFullscreen?.();
    }
  };

  const act = async (work: () => Promise<unknown>, fallback: string) => {
    try {
      await work();
      setActionError(null);
      await reload();
      return true;
    } catch (err) {
      setActionError(message(err, fallback));
      return false;
    }
  };

  const acceptLearned = async () => {
    if (!current) {
      return;
    }
    const ok = await act(
      () => patchUnit(current.unit_id, { status: "confirmed" }),
      "could not accept the name",
    );
    if (ok && annotation?.source === "library") {
      await save({});
    }
  };

  const rename = {
    onRenameAll: (unit: string, mark: string) =>
      act(() => patchUnit(unit, { mark }), "could not rename the type"),
    onMerge: async (unit: string, into: string) => {
      const ok = await act(() => mergeUnit(unit, into), "could not merge the types");
      if (ok) {
        await navigate(typeUrl(into), { replace: true });
      }
      return ok;
    },
    onDecide: (instanceId: string, body: ReviewCreate) =>
      act(() => reviewInstance(instanceId, body), "could not relabel the panel"),
  };

  if (loaded && !error && configurations.length === 0) {
    return (
      <Shell onClose={() => void navigate(takeoffUrl)}>
        <p className="m-auto text-sm text-muted">This take-off has no configurations yet.</p>
      </Shell>
    );
  }
  if (loaded && unitId && found === -1 && configurations.length) {
    return (
      <Shell onClose={() => void navigate(takeoffUrl)}>
        <div className="m-auto flex flex-col items-center gap-3">
          <p className="text-sm text-muted">That type is not in this take-off any more.</p>
          <Button onClick={() => go(0)}>Start from the first type</Button>
        </div>
      </Shell>
    );
  }

  return (
    <main className="flex h-full flex-col bg-bg" aria-label="Type review">
      <header className="flex h-14 shrink-0 items-center gap-3 border-b border-line bg-surface px-4">
        <Button asChild variant="ghost" size="sm" aria-label="Back to the take-off">
          <Link to={takeoffUrl}>
            <X aria-hidden="true" />
            <span className="hidden md:inline">Take-off</span>
          </Link>
        </Button>
        <span aria-hidden="true" className="h-5 w-px bg-line-strong" />
        <div className="flex items-center gap-1">
          <Button
            size="sm"
            variant="ghost"
            className="w-8 px-0"
            aria-label="Previous type"
            disabled={index <= 0}
            onClick={() => go(index - 1)}
          >
            <ChevronLeft aria-hidden="true" />
          </Button>
          <span className="tabular min-w-24 text-center text-[13px] font-medium text-ink-2">
            {current ? `Type ${index + 1} of ${configurations.length}` : " "}
          </span>
          <Button
            size="sm"
            variant="ghost"
            className="w-8 px-0"
            aria-label="Next type"
            disabled={index >= configurations.length - 1}
            onClick={() => go(index + 1)}
          >
            <ChevronRight aria-hidden="true" />
          </Button>
        </div>
        <p className="hidden min-w-0 flex-1 truncate text-xs text-muted lg:block">
          <Kbd>←</Kbd> <Kbd>→</Kbd> types · <Kbd>↑</Kbd> <Kbd>↓</Kbd> panels · <Kbd>Esc</Kbd> back
        </p>
        <Button size="sm" variant="ghost" className="ml-auto" onClick={toggleFull}>
          {full ? <Shrink aria-hidden="true" /> : <Expand aria-hidden="true" />}
          {full ? "Exit full screen" : "Full screen"}
        </Button>
      </header>

      {error || loadError ? (
        <div className="border-b border-line bg-surface px-4 py-2">
          <Notice tone="danger">{error ?? loadError}</Notice>
        </div>
      ) : null}

      <div className="flex min-h-0 flex-1">
        <section className="flex min-w-0 flex-1 flex-col" aria-label="Panel">
          <div className="relative min-h-0 flex-1">
            {shown && shownWindow && current ? (
              <TypeViewer
                src={`${API_PREFIX}/documents/${id}/pages/${shown.page_index}/l1.png`}
                window={shownWindow}
                focus={shown.bbox_norm}
                others={others}
                colour={current.colour}
                pan={mode === "pan"}
                label={`${current.mark} on sheet ${shown.sheet_number ?? shown.page_index + 1}`}
              >
                {(geometry) => (
                  <AnnotationLayer
                    geometry={geometry}
                    shapes={pageShapes}
                    pageId={shown.page_id}
                    mode={mode}
                    colour={colour}
                    text={label}
                    selected={selected}
                    onSelect={setSelected}
                    onDraw={draw}
                  />
                )}
              </TypeViewer>
            ) : (
              <div className="grid h-full place-items-center bg-stage">
                {loadError ? null : <Spinner />}
              </div>
            )}
            {shown && annotation && canReview ? (
              <div className="absolute top-3 left-3">
                <AnnotationToolbar
                  mode={mode}
                  onMode={(m) => {
                    setMode(m);
                    setSelected(null);
                  }}
                  colour={colour}
                  onColour={setColour}
                  text={label}
                  onText={setLabel}
                  canUndo={history.length > 0}
                  onUndo={undo}
                  canDelete={selected !== null}
                  onDelete={removeSelected}
                />
              </div>
            ) : null}
          </div>
          <footer className="shrink-0 border-t border-line bg-surface">
            <div className="flex items-center gap-3 px-4 pt-2 text-xs text-muted">
              <span>
                {shown
                  ? `Sheet ${shown.sheet_number ?? shown.page_index + 1} · ${shown.floor ?? "floor not read"} · bay ${shown.bay ?? "?"} · ${feetInches(shown.width_ft)} × ${feetInches(shown.height_ft)}`
                  : " "}
              </span>
              {shown && shown.id === referenceId ? (
                <Badge tone="accent">Reference</Badge>
              ) : shown && canReview ? (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => void save({ reference_instance_id: shown.id })}
                >
                  Use as reference
                </Button>
              ) : null}
            </div>
            <ul aria-label="Panels of this type" className="flex gap-2 overflow-x-auto px-4 py-2">
              {panels.map((p) => (
                <li key={p.id} className="shrink-0">
                  <button
                    type="button"
                    aria-pressed={p.id === shownId}
                    aria-label={`Panel on sheet ${p.sheet_number ?? p.page_index + 1}, ${p.floor ?? "floor not read"}, bay ${p.bay ?? "?"}`}
                    onClick={() => setShownId(p.id)}
                    className={cn(
                      "grid size-16 place-items-center overflow-hidden rounded-md border bg-stage",
                      p.id === shownId ? "border-accent ring-2 ring-accent" : "border-line",
                    )}
                  >
                    <img src={p.crop_url} alt="" loading="lazy" className="max-h-full max-w-full" />
                  </button>
                </li>
              ))}
            </ul>
          </footer>
        </section>

        <aside
          className="flex w-96 shrink-0 flex-col gap-5 overflow-y-auto border-l border-line bg-surface p-5"
          aria-label="About this type"
        >
          {current ? (
            <>
              <header className="flex items-start gap-3">
                <span
                  aria-hidden="true"
                  className="mt-1.5 size-4 shrink-0 rounded"
                  style={{ background: current.colour }}
                />
                <div className="min-w-0 flex-1">
                  <h1
                    className={cn(
                      "truncate font-mono text-xl font-semibold",
                      current.named ? "text-ink" : "text-muted italic",
                    )}
                  >
                    {current.mark}
                  </h1>
                  {current.summary ? (
                    <p className="text-[13px] text-muted">{current.summary}</p>
                  ) : null}
                </div>
                {canReview ? (
                  <Button size="sm" variant="secondary" onClick={() => setRenaming(true)}>
                    <Pencil aria-hidden="true" />
                    Rename
                  </Button>
                ) : null}
              </header>

              {current.learned_from ? (
                <div className="flex flex-col gap-2 rounded-lg border border-violet/30 bg-violet-soft px-3 py-2.5">
                  <p className="flex items-center gap-2 text-[13px] text-ink-2">
                    <Sparkles aria-hidden="true" className="size-4 text-violet" />
                    Learned from {current.learned_from.filename ?? "an earlier project"}
                  </p>
                  {current.code_parts ? <CodePartsRow parts={current.code_parts} /> : null}
                  {canReview ? (
                    <Button
                      size="sm"
                      variant="primary"
                      className="self-start"
                      onClick={() => void acceptLearned()}
                    >
                      Accept learned name
                    </Button>
                  ) : null}
                </div>
              ) : null}

              {actionError ? (
                <p className="text-[13px] text-danger" role="alert">
                  {actionError}
                </p>
              ) : null}

              <dl className="grid grid-cols-2 gap-2 text-sm">
                <div className="rounded-lg bg-sunken px-3 py-2">
                  <dt className="text-[11px] font-medium tracking-wide text-muted uppercase">
                    Panels
                  </dt>
                  <dd className="tabular mt-0.5 font-semibold text-ink">
                    {current.count.toLocaleString("en-US")}
                  </dd>
                </div>
                <div className="rounded-lg bg-sunken px-3 py-2">
                  <dt className="text-[11px] font-medium tracking-wide text-muted uppercase">
                    Sizes
                  </dt>
                  <dd className="tabular mt-0.5 font-semibold text-ink">{current.sizes.length}</dd>
                </div>
              </dl>
              {current.sizes.length ? (
                <ul className="flex flex-col gap-1 text-xs">
                  {current.sizes.slice(0, 8).map((s) => (
                    <li
                      key={`${s.width}x${s.height}`}
                      className="tabular flex justify-between text-ink-2"
                    >
                      <span>
                        {s.width} × {s.height}
                      </span>
                      <span className="text-muted">{s.count}</span>
                    </li>
                  ))}
                </ul>
              ) : null}

              {elsewhere.length ? (
                <OtherSheets shapes={elsewhere} panels={panels} onShow={setShownId} />
              ) : null}

              <Notes
                key={current.unit_id}
                notes={annotation?.notes ?? null}
                details={annotation?.details ?? null}
                learned={annotation?.source === "library"}
                onSave={save}
                readOnly={!canReview}
              />
              <p className="h-4 text-xs text-muted" aria-live="polite">
                {saveState === "saving" ? "Saving…" : saveState === "saved" ? "Saved" : ""}
                {noteError ? <span className="text-danger">{noteError}</span> : null}
              </p>
            </>
          ) : (
            <Spinner />
          )}
        </aside>
      </div>

      {current && renaming ? (
        <RenameDialog
          open
          onOpenChange={(open) => setRenaming(open)}
          configuration={current}
          configurations={configurations}
          instanceId={shown?.id ?? null}
          defaultScope="all"
          error={actionError}
          {...rename}
        />
      ) : null}
    </main>
  );
}

function Shell({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <main className="flex h-full flex-col bg-bg" aria-label="Type review">
      <header className="flex h-14 items-center border-b border-line bg-surface px-4">
        <Button variant="ghost" size="sm" onClick={onClose}>
          <X aria-hidden="true" />
          Take-off
        </Button>
      </header>
      <div className="flex flex-1">{children}</div>
    </main>
  );
}

/**
 * Notes and details, saved when the estimator leaves a field that changed. They start
 * empty until the annotation has loaded, and are not editable before that.
 */
function Notes({
  notes,
  details,
  learned,
  onSave,
  readOnly,
}: {
  notes: string | null;
  details: TypeDetails | null;
  learned: boolean;
  onSave: (change: { notes?: string; details?: TypeDetails }) => Promise<boolean>;
  /** Shown, not editable, for a role that may not review. */
  readOnly: boolean;
}) {
  const [draft, setDraft] = useState(notes ?? "");
  const [fields, setFields] = useState<TypeDetails | null>(details);

  useEffect(() => {
    setDraft(notes ?? "");
  }, [notes]);
  useEffect(() => {
    setFields(details);
  }, [details]);

  const ready = !readOnly && notes !== null && details !== null && fields !== null;

  return (
    <section aria-label="Notes and details" className="flex flex-col gap-3">
      <label className="flex flex-col gap-1 text-[13px] font-medium text-ink-2">
        <span className="flex items-center gap-2">
          Notes
          {learned ? <Badge tone="violet">From the library</Badge> : null}
        </span>
        <textarea
          aria-label="Notes"
          className={cn(FIELD, "min-h-28 py-2")}
          value={draft}
          disabled={!ready}
          placeholder="Anything the next estimator should know about this type"
          onChange={(event) => setDraft(event.target.value)}
          onBlur={() => {
            if (ready && draft !== notes) {
              void onSave({ notes: draft });
            }
          }}
        />
      </label>
      {DETAILS.map(({ key, label }) => (
        <label key={key} className="flex flex-col gap-1 text-[13px] font-medium text-ink-2">
          <span>{label}</span>
          <input
            type="text"
            aria-label={label}
            className={cn(FIELD, "h-8")}
            value={fields?.[key] ?? ""}
            disabled={!ready}
            onChange={(event) => setFields((f) => (f ? { ...f, [key]: event.target.value } : f))}
            onBlur={() => {
              if (!ready || !fields) {
                return;
              }
              const value = (fields[key] ?? "").trim() || null;
              if (value !== (details[key] ?? null)) {
                void onSave({ details: { ...details, [key]: value } });
              }
            }}
          />
        </label>
      ))}
    </section>
  );
}

/** Marks drawn on other sheets of the type, with a way to go and look at them. */
function OtherSheets({
  shapes,
  panels,
  onShow,
}: {
  shapes: readonly Shape[];
  panels: readonly Instance[];
  onShow: (panelId: string) => void;
}) {
  const byPage = new Map<string, number>();
  for (const s of shapes) {
    byPage.set(s.page_id, (byPage.get(s.page_id) ?? 0) + 1);
  }
  return (
    <section aria-label="Marks on other sheets" className="flex flex-col gap-1.5">
      <h2 className="text-xs font-semibold tracking-wide text-muted uppercase">
        Marks on other sheets
      </h2>
      <ul className="flex flex-col gap-1 text-[13px]">
        {[...byPage].map(([pageId, n]) => {
          const panel = panels.find((p) => p.page_id === pageId);
          const where = panel
            ? `Sheet ${panel.sheet_number ?? panel.page_index + 1}`
            : "Another sheet";
          return (
            <li key={pageId} className="flex items-center justify-between gap-2 text-ink-2">
              <span>
                {where} · {n} mark{n === 1 ? "" : "s"}
              </span>
              {panel ? (
                <Button size="sm" variant="ghost" onClick={() => onShow(panel.id)}>
                  Show
                </Button>
              ) : null}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
